import React, { useState } from 'react';
import SimpleDemoModal from './SimpleDemoModal';
import { trackScheduleDemoClick, trackFreeTrialClick, trackGetQuoteClick } from '../utils/analytics';

const Pricing: React.FC = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalType, setModalType] = useState<'demo' | 'trial' | 'quote'>('demo');

  const openModal = (type: 'demo' | 'trial' | 'quote', buttonLocation: string = 'pricing_section') => {
    // Track button clicks based on type
    switch (type) {
      case 'demo':
        trackScheduleDemoClick(buttonLocation, { plan_type: 'demo_request' });
        break;
      case 'trial':
        trackFreeTrialClick(buttonLocation, { plan_type: 'free_trial' });
        break;
      case 'quote':
        trackGetQuoteClick(buttonLocation, { plan_type: 'custom_quote' });
        break;
    }
    
    setModalType(type);
    setIsModalOpen(true);
  };

  const plans = [
    {
      name: "Free Trial",
      description: "Perfect to get a hands-on experience with the product",
      price: 0,
      subText: "5-Day Trial",
      icon: "🚀",
      gradient: "from-blue-500 to-cyan-500",
      features: [
        "10 AI Resumes Analysis",
        "2 AI Calls",
        "Support Included",
        "No Credit Card Required"
      ],
      highlight: false
    },
    {
      name: "Volume Based Pricing",
      description: "Flexible solution for your hiring needs",
      price: "Custom",
      subText: "Contact us",
      icon: "🏢",
      gradient: "from-emerald-500 to-teal-500",
      features: [
        "Flexible limits for AI Resumes Analysis",
        "Flexible limits for AI Calls",
        "Unlimited Email Communications",
        "Priority Support",
      ],
      highlight: false,
      isCustom: true
    }
  ];

  // const addOns = [
  //   {
  //     name: "Custom AI Training",
  //     description: "Train AI models on your specific industry and requirements",
  //     price: "$2,500",
  //     period: "one-time setup"
  //   },
  //   {
  //     name: "White-label Solution",
  //     description: "Complete branding customization for your organization",
  //     price: "$5,000",
  //     period: "one-time setup"
  //   },
  //   {
  //     name: "Dedicated Implementation",
  //     description: "Personal onboarding specialist for seamless setup",
  //     price: "$1,200",
  //     period: "one-time"
  //   }
  // ];

  return (
    <section id="pricing" className="pt-16 pb-24 md:pt-20 md:pb-32 bg-black relative overflow-hidden">
      <div className="max-w-7xl mx-auto px-6 relative z-10">
        {/* Section Header */}
        <div className="text-center mb-20">
          <div className="animate-fadeInUp">
            <p className="section-label">PRICING</p>
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
              Simple, Transparent Pricing
            </h2>
          </div>
          <div className="animate-fadeInUp" style={{ animationDelay: '0.1s' }}>
            <p className="text-base text-gray-300 max-w-2xl mx-auto">
              Simple, transparent plans — no hidden fees.
            </p>
          </div>
        </div>

        {/* Pricing Cards */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-16 justify-center max-w-5xl mx-auto">
          {plans.map((plan, index) => {
            const colorClasses = [
              { cardBg: 'bg-blue-900/30', cardBorder: 'border-blue-600/50', iconBg: 'bg-blue-800', badgeBg: 'bg-blue-800', badgeBorder: 'border-blue-600', badgeText: 'text-blue-200', checkColor: 'text-blue-400' },
              { cardBg: 'bg-emerald-900/30', cardBorder: 'border-emerald-600/50', iconBg: 'bg-emerald-800', badgeBg: 'bg-emerald-800', badgeBorder: 'border-emerald-600', badgeText: 'text-emerald-200', checkColor: 'text-emerald-400' },
            ];
            const colors = colorClasses[index];
            
            return (
              <div
                key={index}
                className="animate-fadeInUp"
                style={{ animationDelay: `${0.2 + index * 0.1}s` }}
              >
                {/* Main card */}
                <div className={`p-6 h-full rounded-2xl border-2 ${colors.cardBg} ${colors.cardBorder} transition-all duration-200 ease-out hover:border-opacity-100`}>
                  {/* Header section */}
                  <div className="mb-6">
                    <div className="flex items-center gap-3 mb-4">
                      <div className={`w-10 h-10 rounded-lg ${colors.iconBg} flex items-center justify-center border ${colors.badgeBorder}`}>
                        <span className="text-lg">{plan.icon}</span>
                      </div>
                      <div>
                        <h3 className="text-base font-bold text-white">{plan.name}</h3>
                        <p className="text-gray-300 text-xs">{plan.description}</p>
                      </div>
                    </div>

                    {/* Price section */}
                    <div className="mb-6">
                      <div className="flex items-baseline gap-2 mb-2">
                        <span className="text-3xl font-bold text-white">
                          {plan.isCustom ? 'Custom' : `$${plan.price}`}
                        </span>
                        {!plan.isCustom && (
                          <span className="text-sm text-gray-400 font-medium">USD</span>
                        )}
                      </div>
                      <div className={`inline-flex items-center px-3 py-1 rounded-lg ${colors.badgeBg} border ${colors.badgeBorder}`}>
                        <span className={`text-xs font-medium ${colors.badgeText}`}>{plan.subText}</span>
                      </div>
                    </div>
                  </div>

                  {/* Features section */}
                  <div className="mb-6">
                    <h4 className="text-xs font-semibold text-white uppercase tracking-wide mb-3">What's included</h4>
                    <ul className="space-y-2.5 mb-6">
                      {plan.features.map((feature, featureIndex) => (
                        <li key={featureIndex} className="flex items-start gap-2">
                          <div className={`w-5 h-5 rounded-md ${colors.iconBg} flex items-center justify-center mt-0.5 flex-shrink-0 border ${colors.badgeBorder}`}>
                            <svg className={`w-3 h-3 ${colors.checkColor}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
                            </svg>
                          </div>
                          <span className="text-gray-300 text-sm">{feature}</span>
                        </li>
                      ))}
                    </ul>
                  </div>

                  {/* CTA Button */}
                  <button 
                    className="w-full btn-primary"
                    onClick={() => openModal(plan.isCustom ? 'quote' : 'trial', `pricing_card_${plan.name.toLowerCase().replace(' ', '_')}`)}
                  >
                    {plan.isCustom ? 'Get a Quote →' : 'Start Free Trial →'}
                  </button>
                </div>
              </div>
            );
          })}
        </div>

        {/* FAQ Section */}
        <div id='faq' className="animate-fadeInUp" style={{ animationDelay: '0.4s' }}>
          <div className="p-8 md:p-10 rounded-2xl border-2 border-white/20 transition-all duration-200 ease-out hover:border-white/30 hover:bg-[#ffffff22] bg-[#ffffff1a]">
            <div className="text-center mb-10">
              <p className="section-label">FAQ</p>
              <h3 className="text-2xl md:text-3xl font-bold text-white">Frequently Asked Questions</h3>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-6 mb-8">
              <div className="transition-colors duration-200 hover:bg-white/5 rounded-lg p-3 -m-3">
                <h4 className="font-semibold text-white mb-2">Is there a free trial?</h4>
                <p className="text-gray-300 text-sm">Yes, we offer a 5-day free trial. No credit card required to get started.</p>
              </div>
              <div className="transition-colors duration-200 hover:bg-white/5 rounded-lg p-3 -m-3">
                <h4 className="font-semibold text-white mb-2">In AI calls, can I set my own questions for the candidates?</h4>
                <p className="text-gray-300 text-sm">Yes, you can fully customize the questions. The AI will ask candidates exactly what you define.</p>
              </div>
              <div className="transition-colors duration-200 hover:bg-white/5 rounded-lg p-3 -m-3">
                <h4 className="font-semibold text-white mb-2">Does the AI automatically schedule calendar invites?</h4>
                <p className="text-gray-300 text-sm">Yes. Once a candidate confirms a slot, the AI creates the calendar invite and sends it to both you and the candidate.</p>
              </div>
              <div className="transition-colors duration-200 hover:bg-white/5 rounded-lg p-3 -m-3">
                <h4 className="font-semibold text-white mb-2">Do you provide support if I face issues?</h4>
                <p className="text-gray-300 text-sm">Yes, we offer dedicated support to help with onboarding and to quickly resolve any issues.</p>
              </div>
            </div>

            <div className="text-center pt-6 border-t border-white/20">
              <button 
                className="btn-primary mb-3 transition-all duration-200 hover:scale-105 active:scale-95"
                onClick={() => openModal('trial', 'faq_section_cta')}
              >
                Start Your Free Trial →
              </button>
              <p className="text-sm text-gray-400">
                5-day free trial — no credit card required
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Demo Modal */}
      <SimpleDemoModal 
        isOpen={isModalOpen} 
        onClose={() => setIsModalOpen(false)}
        modalType={modalType}
      />
    </section>
  );
};

export default Pricing; 